/**
 * Staff calls, held above the screens.
 *
 * Mounted once, in the shell, so a call survives the person moving from the
 * work register to the map to the store: the engine, the ring and the call
 * window live here, and every screen merely asks for them. The window and
 * the ring card are drawn through a portal at the top of the page's stacking
 * order, above Sky's dock and every dialog — a call is the one thing that
 * must not disappear behind somebody's work.
 *
 * On the platform it runs only for a sign-in that holds `calls:use`; one
 * without it never polls. Should the platform still refuse (a role changed
 * mid-session), the engine stops and the feature hides itself.
 *
 * Four signals carry a ring, because any one of them can fail: the card,
 * the tone (silent until the page has been touched — a browser rule), the
 * tab's title, and a system notification if the person allowed them on the
 * Staff calls screen. The page never asks for notification permission by
 * itself; a prompt at load is the prompt everybody declines.
 *
 * v2 adds here: the person's chosen ringtone and its volume; their Do not
 * disturb (from the poll, set from the header menu or the Staff calls
 * screen); which side panel the call window shows; and the meeting reminders
 * — fifteen and five minutes before, and once live — each shown once per
 * meeting and stage on this device, with the quieter chime.
 */
import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
  type ReactNode,
} from "react";
import { createPortal } from "react-dom";
import { useQueryClient } from "@tanstack/react-query";
import { useSession } from "../../app/SessionContext";
import { useCallDirectory, useCallDnd, useCallHistory, useSetCallDnd } from "../../api/queries";
import { principalKey } from "../../assistant/preferences";
import type { Call, CallMedia, DndState, Meeting, ReminderStage } from "../../domain/calls";
import {
  DND_OFF,
  dndActive,
  dndUntilFor,
  historyOutcome,
  meOn,
  reminderStage,
  sortUpcoming,
  type DndChoice,
} from "../../domain/calls";
import { t } from "../../i18n";
import { CallEngine, type CallTransport, type EngineState } from "./engine";
import { playOnce, readRingSetting, REMINDER_TONE, startRing, writeRingSetting, type RingSetting } from "./ringtone";
import { CallLayer } from "./CallWindow";
import type { DataSource } from "../../api/dataSource";

export type WindowSize = "mini" | "float" | "large";
export type CallPanel = "people" | "chat" | "settings";

export interface Reminder {
  key: string;
  meeting: Meeting;
  stage: ReminderStage;
}

export interface CallsApi {
  available: boolean;
  state: EngineState;
  engine: CallEngine;
  /** the sample: nobody is on the other end, and every call says so */
  sample: boolean;
  size: WindowSize;
  setSize: (s: WindowSize) => void;
  ringMuted: boolean;
  setRingMuted: (m: boolean) => void;
  /** missed calls since the Staff calls screen was last opened */
  missed: number;
  markSeen: () => void;
  simulateIncoming?: (media: CallMedia) => Promise<void>;
  /* ── v2 ── */
  ring: RingSetting;
  setRing: (r: RingSetting) => void;
  /** my Do not disturb, on now or not */
  dnd: DndState;
  dndOn: boolean;
  /** turn it on for a while, or off (null); `allow` keeps the list when left out */
  setDnd: (choice: DndChoice | null, allow?: string[]) => Promise<void>;
  /** the same, with the end already worked out: an ISO time, "forever", or null for off */
  setDndUntil: (until: string | "forever" | null, allow: string[]) => Promise<void>;
  dndBusy: boolean;
  dndError?: string;
  /** the side panel of the call window, opened large */
  panel: CallPanel | null;
  openPanel: (p: CallPanel | null) => void;
  reminders: Reminder[];
  dismissReminder: (key: string) => void;
}

const Ctx = createContext<CallsApi | null>(null);

/** The calls feature, or null where it is not running (no provider, no permission). */
export function useCalls(): CallsApi | null {
  return useContext(Ctx);
}

export function transportFor(source: DataSource): CallTransport {
  return {
    poll: (after, afterMsg) => source.callsPoll(after, afterMsg),
    create: (invitees, media, opts) => source.callsCreate(invitees, media, opts),
    answer: (id, video) => source.callsAnswer(id, video),
    decline: (id) => source.callsDecline(id),
    leave: (id) => source.callsLeave(id),
    invite: (id, invitees) => source.callsInvite(id, invitees),
    signal: (id, to, kind, payload) => source.callsSignal(id, to, kind, payload),
    media: (id, a, v, sharing) => source.callsMedia(id, a, v, sharing),
    ice: () => source.callsIce(),
    join: (id) => source.callsJoin(id),
    startMeeting: (id) => source.callsStartMeeting(id),
    settings: (id, patch) => source.callsSettings(id, patch),
    admit: (id, who, allow) => source.callsAdmit(id, who, allow),
    remove: (id, who) => source.callsRemove(id, who),
    mute: (id, who) => source.callsMute(id, who),
    role: (id, who, role) => source.callsRole(id, who, role),
    end: (id) => source.callsEnd(id),
    hand: (id, up) => source.callsHand(id, up),
    messages: (id, after) => source.callsMessages(id, after),
    sendMessage: (id, text) => source.callsSendMessage(id, text),
  };
}

const RING_MUTE_KEY = "sky.calls.ringMuted";
const REMINDED_KEY = "sky.calls.reminded";

function readReminded(): string[] {
  try {
    const v = JSON.parse(localStorage.getItem(REMINDED_KEY) ?? "[]") as unknown;
    return Array.isArray(v) ? v.filter((x): x is string => typeof x === "string") : [];
  } catch {
    return [];
  }
}
const seenKey = (who: string) => `sky.calls.seen.${who}`;

function readStore(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function writeStore(key: string, value: string | null) {
  try {
    if (value == null) localStorage.removeItem(key);
    else localStorage.setItem(key, value);
  } catch {
    /* storage unavailable: the choice lasts as long as the page */
  }
}

/** Whether this sign-in may use staff calls at all, as far as the client can tell. */
export function callsPermitted(source: DataSource, permissions: string[]): boolean {
  if (source.kind === "mock") return true;
  return permissions.includes("calls:use") || permissions.includes("*");
}

export function CallsProvider({ children }: { children: ReactNode }) {
  const session = useSession();
  const permitted = callsPermitted(session.source, session.principal.permissions);
  if (!permitted) return <>{children}</>;
  return <Running>{children}</Running>;
}

function Running({ children }: { children: ReactNode }) {
  const session = useSession();
  const qc = useQueryClient();
  const who = principalKey(session.principal);
  const directory = useCallDirectory();
  const dirRef = useRef(directory.data);
  dirRef.current = directory.data;

  const engine = useMemo(
    () =>
      new CallEngine({
        transport: transportFor(session.source),
        /* the platform does not yet name me in the poll: I am the one on the call the directory does not list */
        guessMe: (call) => {
          const known = new Set((dirRef.current?.items ?? []).map((p) => p.principalId));
          if (!known.size) return undefined;
          const unknown = call.participants.filter((p) => !known.has(p.principalId));
          return unknown.length === 1 ? unknown[0].principalId : undefined;
        },
      }),
    [session.source],
  );

  const state = useSyncExternalStore(
    (cb) => engine.subscribe(cb),
    () => engine.getState(),
    () => engine.getState(),
  );

  useEffect(() => {
    engine.start();
    /* the cadence follows visibility; coming back to the tab asks at once */
    const onVis = () => {
      if (!document.hidden) engine.pollNow();
    };
    document.addEventListener("visibilitychange", onVis);
    return () => {
      document.removeEventListener("visibilitychange", onVis);
      engine.stop();
    };
  }, [engine]);

  const [size, setSize] = useState<WindowSize>("float");
  const [ringMuted, setRingMutedState] = useState(() => readStore(RING_MUTE_KEY) === "1");
  const setRingMuted = (m: boolean) => {
    setRingMutedState(m);
    writeStore(RING_MUTE_KEY, m ? "1" : null);
  };

  /* a new call opens as the floating card, not wherever the last one was left */
  const callId = state.call?.id;
  useEffect(() => {
    if (callId) setSize("float");
  }, [callId]);

  /* ── the ring ───────────────────────────────────────────────────────── */
  const ringing = state.incoming[0];
  const ringingId = ringing?.id;
  const [ring, setRingState] = useState<RingSetting>(() => readRingSetting());
  const setRing = (r: RingSetting) => {
    setRingState(r);
    writeRingSetting(r);
  };
  useEffect(() => {
    if (!ringingId || ringMuted) return;
    return startRing(ring.tone, ring.volume);
  }, [ringingId, ringMuted, ring.tone, ring.volume]);

  useEffect(() => {
    if (!ringing) return;
    const original = document.title;
    const caller = ringing.startedByName ?? t("A colleague");
    let on = false;
    const timer = setInterval(() => {
      on = !on;
      document.title = on ? t("Incoming call — {name}", { name: caller }) : original;
    }, 1000);
    return () => {
      clearInterval(timer);
      document.title = original;
    };
    // the caller of one ring does not change while it rings
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ringingId]);

  const notified = useRef(new Set<string>());
  useEffect(() => {
    for (const c of state.incoming) {
      if (notified.current.has(c.id)) continue;
      notified.current.add(c.id);
      notify(c);
    }
  }, [state.incoming]);

  /* a ring that stopped, or a call that ended, may be a missed call: read the history again */
  const ringCount = state.incoming.length;
  const prevRing = useRef(ringCount);
  useEffect(() => {
    if (ringCount < prevRing.current || state.ended) void qc.invalidateQueries({ queryKey: ["calls-history"] });
    prevRing.current = ringCount;
  }, [ringCount, state.ended, qc]);

  /* ── missed since last visit ────────────────────────────────────────── */
  const history = useCallHistory(state.available);
  const [seenAt, setSeenAt] = useState(() => readStore(seenKey(who)) ?? "");
  const known = new Set((directory.data?.items ?? []).map((p) => p.principalId));
  const missed = (history.data ?? []).filter(
    (c) => historyOutcome(c, meOn(c, state.me, known)) === "missed" && c.createdAt > seenAt,
  ).length;
  const markSeen = () => {
    const now = new Date().toISOString();
    setSeenAt(now);
    writeStore(seenKey(who), now);
  };

  /* ── do not disturb ─────────────────────────────────────────────────── */
  const dndQuery = useCallDnd(state.available && !state.dnd);
  const dnd = state.dnd ?? dndQuery.data ?? DND_OFF;
  const saveDnd = useSetCallDnd();
  const [dndError, setDndError] = useState<string>();
  const setDnd = (choice: DndChoice | null, allow?: string[]) =>
    setDndUntil(choice ? dndUntilFor(choice, new Date()) : null, allow ?? dnd.allow.map((a) => a.principalId));
  const setDndUntil = async (until: string | "forever" | null, allow: string[]) => {
    setDndError(undefined);
    try {
      const d = await saveDnd.mutateAsync({ until, allow });
      engine.setDnd(d);
      void qc.invalidateQueries({ queryKey: ["calls-directory"] });
    } catch (e) {
      setDndError((e as { detail?: string }).detail ?? (e as Error).message);
    }
  };

  /* ── the call window's side panel ───────────────────────────────────── */
  const [panel, setPanel] = useState<CallPanel | null>(null);
  const openPanel = (p: CallPanel | null) => {
    setPanel(p);
    if (p) setSize("large");
  };
  useEffect(() => {
    if (!callId) setPanel(null);
  }, [callId]);

  /* ── meeting reminders ──────────────────────────────────────────────── */
  const [reminded, setReminded] = useState<string[]>(() => readReminded());
  const [tick, setTick] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setTick(Date.now()), 15_000);
    return () => clearInterval(id);
  }, []);
  const reminders: Reminder[] = sortUpcoming(state.meetings)
    .map((m) => ({ m, stage: reminderStage(m, tick) }))
    .filter((x): x is { m: Meeting; stage: ReminderStage } => !!x.stage)
    /* the meeting I am on needs no reminder */
    .filter((x) => !(state.call && x.m.callId && state.call.id === x.m.callId))
    .map((x) => ({ key: `${x.m.id}:${x.stage}`, meeting: x.m, stage: x.stage }))
    .filter((r) => !reminded.includes(r.key));
  const dismissReminder = (key: string) => {
    const next = [...new Set([...readReminded(), key])].slice(-200);
    setReminded(next);
    writeStore(REMINDED_KEY, JSON.stringify(next));
  };
  const chimed = useRef(new Set<string>());
  const reminderKeys = reminders.map((r) => r.key).join("|");
  useEffect(() => {
    const fresh = reminderKeys.split("|").filter((k) => k && !chimed.current.has(k));
    if (!fresh.length) return;
    for (const k of fresh) chimed.current.add(k);
    /* the quieter chime, once, and not over a ring or while on Do not disturb */
    if (!ringMuted && !ringingId && !dndActive(dnd, Date.now())) playOnce(REMINDER_TONE, Math.round(ring.volume * 0.6));
    // the volume at the moment of the reminder is the one wanted
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reminderKeys]);

  const source = session.source;
  const api: CallsApi = {
    available: state.available,
    state,
    engine,
    sample: source.kind === "mock",
    size,
    setSize,
    ringMuted,
    setRingMuted,
    missed,
    markSeen,
    simulateIncoming: source.simulateIncomingCall
      ? async (media) => {
          await source.simulateIncomingCall!(media);
          engine.pollNow();
        }
      : undefined,
    ring,
    setRing,
    dnd,
    dndOn: dndActive(dnd, tick),
    setDnd,
    setDndUntil,
    dndBusy: saveDnd.isPending,
    dndError,
    panel,
    openPanel,
    reminders,
    dismissReminder,
  };

  return (
    <Ctx.Provider value={api}>
      {children}
      {state.available && createPortal(<CallLayer />, document.body)}
    </Ctx.Provider>
  );
}

function notify(c: Call) {
  try {
    if (typeof Notification === "undefined" || Notification.permission !== "granted") return;
    const n = new Notification(t("Incoming call"), {
      body: `${c.startedByName ?? t("A colleague")} · ${c.media === "video" ? t("Video call") : t("Voice call")}`,
      tag: `call-${c.id}`,
    });
    n.onclick = () => {
      window.focus();
      n.close();
    };
  } catch {
    /* a browser that lists Notification but refuses to construct one (iOS) */
  }
}
